// Match history. Rendered as a metadata rail (Linear/Vercel-style), NOT
// a card grid or heavy HTML table. Each row: deck A vs deck B in display-
// serif, followed by a compact mono metadata line (record, games, elapsed,
// when). Editorial empty state instead of the generic "No items yet."

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertCircle, Swords } from "lucide-react";
import {
  getPlaytestHistory,
  type GauntletHistoryItem,
  type HistoryItem,
  type PlaytestHistoryJob,
} from "../lib/api";
import EmptyState from "../components/EmptyState";
import Kbd from "../components/Kbd";
import { cn } from "../lib/cn";

function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const delta = Date.now() - then;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function fmtElapsed(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

function winnerSide(j: PlaytestHistoryJob): "a" | "b" | "tie" | null {
  if (j.status !== "complete") return null;
  const a = j.outcome.deck_a_wins;
  const b = j.outcome.deck_b_wins;
  if (a > b) return "a";
  if (b > a) return "b";
  return "tie";
}

export default function PlaytestHistory() {
  const query = useQuery({
    queryKey: ["playtest-history"],
    queryFn: () => getPlaytestHistory(50, 0),
  });

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-10">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Playtest</div>
          <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
            Past matches.
          </h1>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-fg-subtle">
          {query.data && (
            <span className="font-mono tabular-nums">
              {query.data.count} total
            </span>
          )}
          <Link to="/playtest" className="inline-flex items-center gap-1.5 text-fg-muted hover:text-fg">
            Run new
            <Kbd>P</Kbd>
          </Link>
        </div>
      </header>

      {query.isLoading && <div className="text-sm text-fg-muted">Loading…</div>}

      {query.error && (
        <div className="surface p-4 text-sm text-danger flex items-center gap-2">
          <AlertCircle size={14} strokeWidth={1.75} />
          {(query.error as Error).message}
        </div>
      )}

      {query.data && query.data.count === 0 && (
        <EmptyState
          title="No goldfish games yet."
          body="Run a playtest from any deck and the head-to-head log lands here — record, turn-by-turn life chart, and the raw Forge dump if you want to dig in."
          action={
            <Link to="/playtest" className="btn-primary text-sm h-9 px-4">
              Run playtest
            </Link>
          }
          tip={<>tip: press <Kbd>⌘K</Kbd> from any page to jump to playtest</>}
        />
      )}

      {query.data && query.data.count > 0 && (
        <ul className="divide-y divide-border/60">
          {query.data.jobs.map((j) => (
            <HistoryRow key={`${j.kind}:${j.id}`} item={j} />
          ))}
        </ul>
      )}
    </div>
  );
}

function HistoryRow({ item }: { item: HistoryItem }) {
  if (item.kind === "gauntlet") return <GauntletRow g={item} />;
  return <MatchRow job={item} />;
}

function MatchRow({ job }: { job: PlaytestHistoryJob }) {
  const side = winnerSide(job);
  const record =
    job.status === "complete"
      ? `${job.outcome.deck_a_wins}-${job.outcome.deck_b_wins}${job.outcome.draws > 0 ? `-${job.outcome.draws}` : ""}`
      : job.status === "error"
        ? "error"
        : job.status;

  return (
    <li>
      <Link
        to={`/playtest/jobs/${encodeURIComponent(job.id)}`}
        className="group grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 hover:bg-bg-raised/30 -mx-3 px-3 rounded-md transition-colors"
      >
        {/* Row 1: the matchup */}
        <div className="font-display text-lg md:text-xl font-medium tracking-[-0.01em] leading-tight truncate">
          <span className={cn(side === "a" && "text-fg", side === "b" && "text-fg-muted")}>
            {job.deck_a_theme}
          </span>
          <span className="font-sans italic text-fg-subtle font-normal"> vs </span>
          <span className={cn(side === "b" && "text-fg", side === "a" && "text-fg-muted")}>
            {job.deck_b_theme}
          </span>
        </div>
        <div className="font-mono tabular-nums text-sm text-fg-subtle shrink-0 text-right">
          {fmtRelative(job.started_at)}
        </div>

        {/* Row 2: the metadata rail */}
        <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-fg-subtle tabular-nums">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">record</span>
            <span className={cn(
              "text-fg-muted",
              job.status === "complete" && side !== "tie" && "text-fg",
              job.status === "error" && "text-danger",
            )}>
              {record}
            </span>
          </span>
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">games</span>
            <span className="text-fg-muted">{job.games}</span>
          </span>
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">elapsed</span>
            <span className="text-fg-muted">{fmtElapsed(job.elapsed_ms)}</span>
          </span>
          <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-fg-subtle">
            →
          </span>
        </div>
      </Link>
    </li>
  );
}

function GauntletRow({ g }: { g: GauntletHistoryItem }) {
  // Roll-up record: user wins vs everyone else's wins. The total
  // includes draws + dnf, so derive losses as (total - wins) and let
  // draws hide unless they actually exist in the run. The Editor's
  // brief lives on the gauntlet detail page; the history row stays
  // compact.
  const total = g.total_games;
  const wins = g.user_wins;
  const losses = Math.max(0, total - wins);
  const inFlight =
    g.status === "queued" ||
    g.status === "running" ||
    g.status === "summarizing";
  const record =
    g.status === "complete"
      ? `${wins}-${losses}`
      : g.status === "error"
        ? "error"
        : g.status;
  const recordTone =
    g.status === "complete"
      ? wins > losses
        ? "text-fg"
        : "text-fg-muted"
      : g.status === "error"
        ? "text-danger"
        : inFlight
          ? "text-accent"
          : "text-fg-muted";

  return (
    <li>
      <Link
        to={`/mega-build/gauntlet/${encodeURIComponent(g.id)}`}
        className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-x-3 gap-y-1 py-5 hover:bg-bg-raised/30 -mx-3 px-3 rounded-md transition-colors"
        title="Open gauntlet"
      >
        {/* Row 1: gauntlet badge + the deck name */}
        <Swords
          size={14}
          strokeWidth={1.75}
          className="text-fg-muted self-center shrink-0"
          aria-label="Gauntlet"
        />
        <div className="font-display text-lg md:text-xl font-medium tracking-[-0.01em] leading-tight truncate">
          <span className="text-fg">{g.deck_theme}</span>
          <span className="font-sans italic text-fg-subtle font-normal"> vs </span>
          <span className="text-fg-muted">top-meta gauntlet</span>
        </div>
        <div className="font-mono tabular-nums text-sm text-fg-subtle shrink-0 text-right">
          {fmtRelative(g.started_at)}
        </div>

        {/* Row 2: metadata rail (record · games · matchups · elapsed) */}
        <div className="col-span-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-fg-subtle tabular-nums">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">record</span>
            <span className={recordTone}>{record}</span>
          </span>
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">games</span>
            <span className="text-fg-muted">{total}</span>
          </span>
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">matchups</span>
            <span className="text-fg-muted">3 × {g.games_per_archetype}</span>
          </span>
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">elapsed</span>
            <span className="text-fg-muted">{fmtElapsed(g.elapsed_ms)}</span>
          </span>
          <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-fg-subtle">
            →
          </span>
        </div>
      </Link>
    </li>
  );
}
